import React, { useState, useMemo, useRef, useEffect } from "react";
import { format } from "date-fns";
import {
  Typography,
  Stack,
  Button,
  CircularProgress,
  Box,
  Tab,
  Tabs,
  InputAdornment,
  OutlinedInput,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from "@mui/material";
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  Timeline as TimelineIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { 
  useGetProductsWithTariffsQuery, 
  useLazyGetProductsWithTariffsQuery,
  ProductWithTariffs, 
  ProductTariffQueryInput 
} from "@/services/product-catalogue/pc-api";
import { useProductAnalysisPolling, useOrderedDutyColumns } from "@/hooks";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";
import DatePicker from "@/components/date-picker";
import ProductTable from "./components/product-table";
import CreateProductForm from "./components/create-product-form";
import EditProductForm from "./components/edit-product-form";
import DeleteProductDialog from "./components/delete-product-dialog";
import DutyColumnsManager, { DutyColumnsManagerRef } from "./components/duty-columns-manager";
import { RequirePage } from "@/roles/protection";
import CSVBatchUpload from "./components/csv-batch-upload";
import AuthorityTimeline from "./components/authority-timeline";
import ProductHubStats from "./components/product-hub-stats";

const ProductCatalogue: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant, areTenantsLoading } = useAppSelector(
    (state) => state.tenantManagement
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithTariffs | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Listen for navigation event from Duty Calculator to switch to Products tab
  useEffect(() => {
    const handleNavigation = () => {
      setActiveTab(0);
    };

    window.addEventListener("td:navigate-products", handleNavigation);

    return () => {
      window.removeEventListener("td:navigate-products", handleNavigation);
    };
  }, []);

  // Ref for DutyColumnsManager
  const dutyColumnsRef = useRef<DutyColumnsManagerRef>(null);
  
  // Split Button State
  const [openSplitButton, setOpenSplitButton] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpenSplitButton((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenSplitButton(false);
  };

  const handleMenuItemClick = (_event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    if (index === 0) {
      handleBulkUpload();
    }
    setOpenSplitButton(false);
  };
  
  const [triggerGetProducts] = useLazyGetProductsWithTariffsQuery();
  const { orderedColumns: dutyColumns } = useOrderedDutyColumns();

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  // Check if user can manage duty columns (SP_OWNER with page, or TD_ADMIN)
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(undefined, { skip: !(user?.userRole === roles.SPOWNER) });
  const allowedKeys = ((user as any)?.allowedPages as string[] | undefined) ?? tenantPagesResp?.pages ?? [];
  const canManageDutyColumns = useMemo(() => {
    if (user?.userRole === roles.TDADMIN) return true;
    if (user?.userRole === roles.SPOWNER) return allowedKeys.includes('PRODUCT_HUB_DUTY_COLUMNS');
    return false;
  }, [user, allowedKeys]);

  // Get client ID
  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    // For SP roles, require an explicitly selected client. For client roles, use their own user id.
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const queryParams = useMemo(() => {
    return {
      effectiveDate: effectiveDate,
      pageNo: currentPage,
      pageSize: pageSize,
      // Always pass clientId; TD Client uses their tenantId as clientId
      ...(clientId ? { clientId } : {}),
      // For TD Admin with TRADEDIVE_CLIENT, also pass tenantId
      ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
    };
  }, [effectiveDate, currentPage, pageSize, clientId, user, isTDAdminTCD, selectedTenant]);

  // Initialize the new polling system
  const polling = useProductAnalysisPolling({
    queryParams,
    enabled: activeTab === 0
  });

  const commonOptions = useMemo(() => {
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant || !clientId || activeTab !== 0,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: (canSelectClients && !selectedClient) || !clientId || activeTab !== 0,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant, isTDAdminTCD, clientId, activeTab]);

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useGetProductsWithTariffsQuery(queryParams, commonOptions);

  const isLoading = useMemo(() => {
    return [isProductsLoading, areClientsLoading, areTenantsLoading].some(Boolean);
  }, [isProductsLoading, areClientsLoading, areTenantsLoading]);


  const filteredProducts = useMemo(() => {
    if (!productsData?.data) return [];
    if (!searchQuery.trim()) return productsData.data;
    
    const query = searchQuery.toLowerCase().trim();
    return productsData.data.filter((product) => {
      const skuMatch = product.sku?.toLowerCase().includes(query);
      const nameMatch = product.name?.toLowerCase().includes(query);
      const descriptionMatch = product.description?.toLowerCase().includes(query);
      return skuMatch || nameMatch || descriptionMatch;
    });
  }, [productsData?.data, searchQuery]);

  const stats = useMemo(() => {
    if (activeTab !== 0 || !productsData?.data) return { totalProducts: 0, uniqueCountries: 0, uniqueHTS: 0, avgTariffRate: 0 };
    
    const products = productsData.data;
    const countries = new Set(products.map(p => p.countryOfOrigin));
    const htsCodes = new Set(products.map(p => p.htsCode));
    
    const totalTariffRate = products.reduce((sum, product) => {
      const productTotal = product.additionalTariffs.reduce((pSum, tariff) => pSum + tariff.rate, 0);
      return sum + productTotal;
    }, 0);
    
    return {
      totalProducts: productsData.meta.countDocs || 0,
      uniqueCountries: countries.size,
      uniqueHTS: htsCodes.size,
      avgTariffRate: products.length > 0 ? (totalTariffRate / products.length) * 100 : 0,
    };
  }, [productsData, activeTab]);

  const handleDateChange = (date: string | null) => {
    if (date) {
      // DatePicker already provides YYYY-MM-DD; avoid new Date(date) which parses as UTC
      setEffectiveDate(date);
    }
    setCurrentPage(1); // Reset to first page when date changes
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleCreateProduct = () => {
    setIsCreateFormOpen(true);
  };

  const handleCloseCreateForm = () => {
    setIsCreateFormOpen(false);
  };

  const handleProductCreated = () => {
    setIsCreateFormOpen(false);
    // The query will automatically refetch due to cache invalidation
  };

  const handleEditProduct = (product: ProductWithTariffs) => {
    setSelectedProduct(product);
    setIsEditFormOpen(true);
  };

  const handleCloseEditForm = () => {
    setIsEditFormOpen(false);
    setSelectedProduct(null);
  };

  const handleProductUpdated = () => {
    setIsEditFormOpen(false);
    setSelectedProduct(null);
    // The query will automatically refetch due to cache invalidation
  };

  const handleDeleteProduct = (productId: string) => {
    const product = productsData?.data.find(p => p._id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedProduct(null);
  };

  const handleProductDeleted = () => {
    setIsDeleteDialogOpen(false);
    setSelectedProduct(null);
    // The query will automatically refetch due to cache invalidation
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBulkUpload = () => {
    setIsBulkUploadOpen(true);
  };

  const handleCloseBulkUpload = () => {
    setIsBulkUploadOpen(false);
  };

  const handleBulkUploadSuccess = () => {
    setIsBulkUploadOpen(false);
    // The query will automatically refetch due to cache invalidation
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // Fetch all pages
      const exportPageSize = 500;
      const allProducts: ProductWithTariffs[] = [];
      // Use current query params but override page info
      const { pageNo, pageSize, ...downloadBaseQuery } = queryParams;
      
      const first = await triggerGetProducts({ ...downloadBaseQuery, pageNo: 1, pageSize: exportPageSize } as ProductTariffQueryInput).unwrap();
      allProducts.push(...(first?.data ?? []));
      const total = first?.meta?.countDocs ?? allProducts.length;
      const totalPages = Math.max(1, Math.ceil(total / exportPageSize));
      for (let p = 2; p <= totalPages; p++) {
        const res = await triggerGetProducts({ ...downloadBaseQuery, pageNo: p, pageSize: exportPageSize } as ProductTariffQueryInput).unwrap();
        allProducts.push(...(res?.data ?? []));
      }

      if (allProducts.length === 0) return;

      // Determine if Metal Percentage column should be included
      const shouldIncludeMetalPercentage = allProducts.some(product => 
        product.metalPercentage !== null && 
        product.metalPercentage !== undefined && 
        product.metalPercentage < 100
      );

      const headers = ["SKU", "Name", "Description", "HTS Code", "AI Risk Score", "Country of Origin"];
      if (shouldIncludeMetalPercentage) {
        headers.push("Metal Percentage");
      }

      // Build export tariff columns across ALL products to ensure complete coverage
      const exportTariffColumns = (() => {
        const dutyColumnIds = new Set(dutyColumns.map(col => col.id));
        const generalDutyColumns: Array<{ id: string; name: string; displayOrder: number }> = [];
        const unmatchedColumns = new Map<string, string>();

        allProducts.forEach((product: ProductWithTariffs) => {
          product.additionalTariffs.forEach((tariff: any) => {
            if (!dutyColumnIds.has(tariff.columnId)) {
              if (tariff.columnName.toLowerCase().includes('general duty') || 
                  tariff.columnName === 'General Duty Rate' ||
                  !tariff.columnId) {
                if (!generalDutyColumns.find(col => col.id === tariff.columnId)) {
                  generalDutyColumns.push({
                    id: tariff.columnId || 'general-duty',
                    name: tariff.columnName,
                    displayOrder: -1,
                  });
                }
              } else {
                if (!unmatchedColumns.has(tariff.columnId)) {
                  unmatchedColumns.set(tariff.columnId, tariff.columnName);
                }
              }
            }
          });
        });

        const allCols = [
          ...generalDutyColumns,
          ...dutyColumns,
          ...Array.from(unmatchedColumns.entries()).map(([id, name]) => ({ id, name, displayOrder: 999 }))
        ];

        return allCols.filter(col => {
          return allProducts.some((product: ProductWithTariffs) => {
            const tariff = product.additionalTariffs.find((t: any) => 
              t.columnId === col.id || (!t.columnId && col.id === 'general-duty')
            );
            return tariff && tariff.rate > 0;
          });
        });
      })();

      exportTariffColumns.forEach(col => headers.push(col.name));
      headers.push("Total Duty");

      const csvData = allProducts.map((row: ProductWithTariffs) => {
        const baseData = [
          row.sku,
          row.name,
          row.description || "",
          row.htsCode,
          row.riskAnalysis?.riskLevel || "N/A",
          row.countryOfOrigin,
        ];

        if (shouldIncludeMetalPercentage) {
          const metalPercentage = row.metalPercentage !== null && row.metalPercentage !== undefined 
            ? `${row.metalPercentage}%` 
            : "N/A";
          baseData.push(metalPercentage);
        }

        exportTariffColumns.forEach((col: any) => {
          const tariff = row.additionalTariffs.find((t: any) => 
            t.columnId === col.id || (!t.columnId && col.id === 'general-duty')
          );
          baseData.push(tariff ? `${(tariff.rate * 100).toFixed(2)}%` : "0%");
        });

        const totalRate = row.additionalTariffs.reduce((sum, tariff) => sum + (tariff.rate * 100), 0);
        baseData.push(`${totalRate.toFixed(2)}%`);

        return baseData;
      });

      const csvContent = [
        headers.map(header => `"${header}"`).join(","),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "product_catalogue.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsDownloading(false);
    }
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  const renderTabContent = () => {
    if (activeTab === 0) {
      // Products Tab
      return (
        <Stack spacing={3}>
          
          {/* Stats Section */}
          <ProductHubStats stats={stats} />

          {/* Toolbar Section: Search, Date, Actions */}
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <OutlinedInput
              size="small"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to first page when search changes
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'neutral.500' }} />
                </InputAdornment>
              }
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                minWidth: 300,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider'
                }
              }}
            />
            <Stack direction="row" spacing={2}>
              <DatePicker
                value={effectiveDate}
                onChangeValue={handleDateChange}
                sx={{
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': { borderColor: 'divider' },
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                onClick={handleDownload}
                disabled={isDownloading || !productsData?.data.length}
                sx={{
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": { 
                    borderColor: "text.primary",
                    backgroundColor: "action.hover",
                  },
                  borderRadius: "8px",
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  px: 4,
                  py: 1.5,
                  minWidth: 'fit-content',
                  maxWidth: 'fit-content',
                }}
              >
                {isDownloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </Stack>
          </Stack>

          {/* Table Section */}
          <Box>
            {isLoading ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8,
                borderRadius: '10px',
                backgroundColor: '#f7f8f8',
              }}>
                <Stack alignItems="center" spacing={3}>
                  <CircularProgress size={48} sx={{ color: '#1CB4BA' }} />
                  <Typography variant="h6" color="neutral.600">
                    Loading your product hub...
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <ProductTable
                data={filteredProducts}
                currentPage={currentPage}
                pageSize={pageSize}
                isLoading={isProductsFetching}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                totalCount={searchQuery.trim() ? filteredProducts.length : (productsData?.meta.countDocs ?? 0)}
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
                isProductPending={polling.isProductPending}
              />
            )}
          </Box>
        </Stack>
      );
    } else if (activeTab === 1 && canManageDutyColumns) {
      // Duty Columns Tab - enforce route-level page guard
      return (
        <RequirePage pageKey={'PRODUCT_HUB_DUTY_COLUMNS'}>
          <DutyColumnsManager ref={dutyColumnsRef} />
        </RequirePage>
      );
    } else if (activeTab === 2 && canManageDutyColumns) {
      // Authority Timeline Tab
      return <AuthorityTimeline />;
    }
    return null;
  };

  // Dynamic Title Logic
  const getPageTitle = () => {
    if (activeTab === 0) return 'Product Hub';
    if (canManageDutyColumns) {
      if (activeTab === 1) return 'Duty Columns Management';
      if (activeTab === 2) return 'Authority Timeline';
    }
    return 'Product Hub';
  };

  const getPageDescription = () => {
    if (activeTab === 0) return 'Manage your products and view applicable tariffs with real-time duty calculations';
    if (canManageDutyColumns) {
      if (activeTab === 1) return 'Create and manage duty columns with custom tariff rules for enhanced product calculations';
      if (activeTab === 2) return 'Visualize the evolution of trade policies across different authorities over time';
    }
    return 'Manage your products and view applicable tariffs with real-time duty calculations';
  };

  return (
    <Stack spacing={0} sx={{ 
      margin: { 
        xs: '-6px -10px 0 -10px', 
        md: '-12px -20px 0 -20px' 
      },
      width: { 
        xs: 'calc(100% + 20px)', 
        md: 'calc(100% + 40px)' 
      }
    }}>
      {/* Tabs - Slim topbar - edge to edge */}
      {canManageDutyColumns && (
        <Box sx={{ borderBottom: '1px solid #EBEBEB', margin: 0, padding: 0, width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 36, px: 3, margin: 0 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 36,
                  padding: '6px 12px',
                  mr: 0.5,
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 2,
                  bottom: 0,
                },
              }}
            >
              <Tab 
                label="Products" 
                icon={<InventoryIcon sx={{ fontSize: 16 }} />} 
                iconPosition="start"
                sx={{ gap: 0.75, py: 0 }}
              />
              {canManageDutyColumns && (
                <Tab 
                  label="Duty Columns" 
                  icon={<SettingsIcon sx={{ fontSize: 16 }} />} 
                  iconPosition="start"
                  sx={{ gap: 0.75, py: 0 }}
                />
              )}
              {canManageDutyColumns && (
                <Tab 
                  label="Authority Timeline" 
                  icon={<TimelineIcon sx={{ fontSize: 16 }} />} 
                  iconPosition="start"
                  sx={{ gap: 0.75, py: 0 }}
                />
              )}
            </Tabs>
            
            {/* Create Column button for Duty Columns tab */}
            {activeTab === 1 && canManageDutyColumns && (
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => dutyColumnsRef.current?.openCreateForm()}
                sx={{
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  height: 32,
                  padding: '4px 12px',
                  fontSize: '0.875rem',
                  "&:hover": { 
                    backgroundColor: "primary.dark",
                  },
                  borderRadius: "6px",
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Create Column
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Header Section (Title & Desc + Action Buttons) */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 1, px: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getPageDescription()}
          </Typography>
        </Box>
        {/* Action buttons moved here for Products tab */}
        {activeTab === 0 && (
          <Stack direction="row" spacing={2}>
            <React.Fragment>
              <ButtonGroup 
                variant="contained" 
                ref={anchorRef} 
                aria-label="split button"
                sx={{ 
                    boxShadow: 'none',
                    '& .MuiButtonGroup-grouped': {
                        borderColor: 'rgba(255, 255, 255, 0.5) !important',
                    }
                }}
              >
                <Button
                  onClick={handleCreateProduct}
                  startIcon={<AddIcon />}
                  sx={{
                    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), #0B494B",
                    border: "1px solid #FFFFFF",
                    boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                    borderRadius: "8px 0 0 8px",
                    color: "#FFFFFF",
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    padding: '6px 12px',
                    '&:hover': {
                       background: "#093b3d",
                    }
                  }}
                >
                  Add Product
                </Button>
                <Button
                  size="small"
                  aria-controls={openSplitButton ? 'split-button-menu' : undefined}
                  aria-expanded={openSplitButton ? 'true' : undefined}
                  aria-label="select merge strategy"
                  aria-haspopup="menu"
                  onClick={handleToggle}
                  sx={{
                    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), #0B494B",
                    border: "1px solid #FFFFFF",
                    borderLeft: "none",
                    boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                    borderRadius: "0 8px 8px 0",
                    color: "#FFFFFF",
                    padding: '6px 8px',
                    '&:hover': {
                       background: "#093b3d",
                    }
                  }}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
              <Popper
                sx={{
                  zIndex: 1,
                }}
                open={openSplitButton}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                placement="bottom-end"
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper elevation={2} sx={{ mt: 1, borderRadius: '8px', border: '1px solid #EBEBEB' }}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList id="split-button-menu" autoFocusItem>
                          <MenuItem
                            onClick={(event) => handleMenuItemClick(event, 0)}
                            sx={{ fontSize: '14px', py: 1, px: 2 }}
                          >
                            <CloudUploadIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                            Bulk Upload (CSV)
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </React.Fragment>
          </Stack>
        )}
      </Stack>

      {/* Tab Content */}
      <Box sx={{ px: 3 }}>
        {renderTabContent()}
      </Box>

      {/* Dialogs - Only for Products tab */}
      {activeTab === 0 && (
        <>
          <CreateProductForm
            open={isCreateFormOpen}
            onClose={handleCloseCreateForm}
            onProductCreated={handleProductCreated}
            onTriggerPolling={polling.addProductPolling}
          />

          <EditProductForm
            open={isEditFormOpen}
            onClose={handleCloseEditForm}
            onProductUpdated={handleProductUpdated}
            product={selectedProduct}
            onTriggerPolling={polling.addProductPolling}
          />

          <DeleteProductDialog
            open={isDeleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            onProductDeleted={handleProductDeleted}
            product={selectedProduct}
          />
        </>
      )}

      {/* Bulk Upload Modal */}
      <CSVBatchUpload
        open={isBulkUploadOpen}
        onClose={handleCloseBulkUpload}
        onSuccess={handleBulkUploadSuccess}
        onTriggerBulkPolling={polling.addBulkPolling}
      />
    </Stack>
  );
};

export default ProductCatalogue;